import BaseService from './BaseService'
import _ from 'lodash'
import _flatten from 'lodash-bound/flatten'
import _initial from 'lodash-bound/initial'
import _map from 'lodash-bound/map'
import _uniq from 'lodash-bound/uniq'
import _without from 'lodash-bound/without'
import { decorator as reusePromise } from 'reuse-promise'
import Hashes from 'jshashes'
import * as errors from '../errors'
import {
  AllowedSendersReason as Reason,
  ConversationAnchorPoints,
  EscalationChangeAction,
  GroupType,
  MessagePriority,
  MessageSenderStatus,
  MessageType,
  ProcessingActionTypes,
} from '../models/enums'
import MessageRecipientStatus, { MESSAGE_STATUS_ORDER } from '../models/enums/MessageRecipientStatus'
import { arrayWrap, jsonCloneDeep } from '../utils'
import { isEmail } from '../utils/email'
import { isPhone } from '../utils/phone'

const md5 = new Hashes.MD5()

const BANG_MESSAGE_TYPES = [
  MessageType.ESCALATION_EXECUTION_CHANGE,
  MessageType.GROUP_MEMBERSHIP_CHANGE,
]

const DEFAULT_FETCH_TIMELINE_ITEMS = 15
const DEFAULT_SELECT_CONVERSATION_ITEMS = 20

const ESCALATION_ACTION_TEXT = {
  [EscalationChangeAction.ACKNOWLEDGED]: 'Acknowledged',
  [EscalationChangeAction.CANCELLED]: 'Cancelled',
  [EscalationChangeAction.INITIATED]: 'Initiated',
  [EscalationChangeAction.MEMBER_ADDED]: 'Added',
  [EscalationChangeAction.NO_RESPONSE]: 'ended with No Response',
}

const GROUP_TYPES_WITH_METADATA = [GroupType.ROLE_ASSIGNMENT, GroupType.ROLE_P2P]

const GROUP_MEMBERSHIP_ACTION_TEXT = {
  ['ADD']: 'added',
  ['JOIN']: 'joined',
  ['LEAVE']: 'left',
  ['REMOVE']: 'removed',
}

const GROUP_MEMBERSHIP_ACTION_PREPOSITION = {
  ['ADD']: 'to',
  ['REMOVE']: 'from',
}

const GROUP_TYPE_LABEL = {
  [GroupType.ESCALATION]: 'escalation',
  [GroupType.FORUM]: 'forum',
  [GroupType.GROUP]: 'group',
  [GroupType.PATIENT_CARE]: 'care team',
  [GroupType.ROLE_P2P]: 'conversation',
  [GroupType.PATIENT_MESSAGING]: 'group',
}

const NEW_OR_DELIVERED = [MessageRecipientStatus.NEW, MessageRecipientStatus.DELIVERED]

const ROLE_ACTION_TEXT = {
  ['OPT_IN']: 'opted into',
  ['OPT_OUT']: 'opted out of',
}

export default class ConversationsService extends BaseService {
  mounted() {
    this._convByConvHandle = {}
    this._fetched = false
    this._rolesFriendsEventPromise = null
    this.__escalationEnabledOrganizations = []
    this.__queuedConversationsToReload = []
    this._roleConversationsProcessingEvent = {}
    this.host.on('message:sending', this._onSendingMessage)
    this.host.on('message:sent', this._onSentMessage)
    this.host.models.Group.on('afterEject', this._onRemoveGroup)
    this.host.models.Group.on('afterInject', this._onChangeGroup)
    this.host.models.Message.on('afterInject', this._onChangeMessage)
    this.host.models.Message.on('afterEject', this._onRemoveMessage)
    this.host.models.Organization.on('afterInject', this._onChangeOrganization)
  }

  dispose() {
    this._convByConvHandle = {}
    this._fetched = false
    this._rolesFriendsEventPromise = null
    this.__escalationEnabledOrganizations = []
    this.__queuedConversationsToReload = []
    this.host.removeListener('message:sending', this._onSendingMessage)
    this.host.removeListener('message:sent', this._onSentMessage)
    this.host.models.Group.removeListener('afterEject', this._onRemoveGroup)
    this.host.models.Group.removeListener('afterInject', this._onChangeGroup)
    this.host.models.Message.removeListener('afterInject', this._onChangeMessage)
    this.host.models.Message.removeListener('afterEject', this._onRemoveMessage)
    this.host.models.Organization.removeListener('afterInject', this._onChangeOrganization)
  }

  clearTimeline(conversation, { emit=true }={}) {
    this.clearMessageStatuses(conversation, { emit: false })

    const { higherContinuation, lowerContinuation, timeline } = conversation
    let messagesCleared = 0
    for (let message of timeline) {
      if (!message.isEphemeral) {
        message.inTimeline = false
        messagesCleared += 1
      }
    }

    conversation.isLive = false
    higherContinuation.itemsEstimate = null
    lowerContinuation.itemsEstimate = null

    if (emit) this.emit('clearTimeline', conversation.id)

    if (this.config.condensedReplays) {
      this.host.models.Conversation.inject(conversation)
    } else {
      this.__reloadConversation(conversation, { notify: false })
    }

    return messagesCleared
  }

  async fetchTimeline(conversationId, {
    anchorPoint=ConversationAnchorPoints.CONVERSATION_END,
    continuation,
    markAsDelivered=true,
    maxItems=DEFAULT_FETCH_TIMELINE_ITEMS
  }={}) {
    let conversation = this.getById(conversationId)
    const { content, _savedFirstUnreadMessage, timeline } = conversation
    let end = content.length
    let itemsAdded = 0
    let messagesToAdd = []
    let start = end - maxItems
    let target = end

    if (this.config.condensedReplays) {
      conversation._savedFirstUnreadMessage = null
    }

    if (anchorPoint === ConversationAnchorPoints.CONVERSATION_END) {
      this.clearTimeline(conversation, { emit: false })
    } else if (anchorPoint === ConversationAnchorPoints.FIRST_UNREAD_ITEM) {
      this.clearTimeline(conversation, { emit: false })

      if (!this.config.condensedReplays) {
        let index = -1
        for (const message of content) {
          index++
          target = index
          if (_savedFirstUnreadMessage && _savedFirstUnreadMessage === message) break
        }
        if (target < content.length) {
          target = Math.min(target + maxItems, content.length)
        }
      }
    } else if (!this.config.condensedReplays && anchorPoint === ConversationAnchorPoints.CONTINUATION) {
      const firstInTimeline = timeline[0] ? timeline[0].sortNumber : Infinity
      const lastInTimeline = timeline[timeline.length - 1] ? timeline[timeline.length - 1].sortNumber : 0
      const sortNumber = continuation === 'LOWER' ? firstInTimeline : lastInTimeline

      if (sortNumber === 0) {
        target = target - maxItems
      } else {
        let index = -1
        for (const message of content) {
          index++
          target = index
          if (continuation === 'HIGHER' && message.sortNumber > sortNumber) {
            break
          }
          if (continuation === 'LOWER' && message.sortNumber >= sortNumber) {
            break
          }
        }
      }
    }

    if (!this.config.condensedReplays) {
      if (anchorPoint !== ConversationAnchorPoints.CONVERSATION_END) {
        if (anchorPoint === ConversationAnchorPoints.CONTINUATION && continuation === 'HIGHER') {
          start = target
          end = start + maxItems
        } else {
          if (target === content.length - 1) {
            target += 1
          }
          end = target
          start = target - maxItems
        }
      }

      end = Math.min(end, content.length + 1)
      start = Math.max(start, 0)

      messagesToAdd.push(...content.slice(start, end))

      for (const message of messagesToAdd) {
        if (!message.inTimeline) {
          message.inTimeline = true
          this.host.models.Message.inject(message)
          itemsAdded += 1
        }
      }
    } else {
      const {
        direction,
        higher_continuation,
        lower_continuation,
        timeline,
      } = await this.host.api.conversations.fetchTimeline(
        conversationId,
        ConversationAnchorPoints.toServer(anchorPoint),
        maxItems,
        {
          continuation,
          markAsDelivered,
        }
      )

      for (const rawMessage of timeline) {
        const { xmlns } = rawMessage

        if (xmlns === 'tigertext:iq:bang') {
          const eventId = rawMessage['bang_id'] || `${rawMessage['sort_number']}`

          if (eventId) {
            this.host.groups.__handleBang({ data: rawMessage, eventId, alwaysAddToTimeline: true })
          } else {
            this.logger.warn('Skipping bang with no event id')
          }
        } else if (xmlns === 'tigertext:iq:message' || xmlns === 'tigertext:iq:group_message') {

          if (!rawMessage.conversation_id) {
            rawMessage.conversation_id = conversationId
          }

          const { message } = this._reactToMessageEventCR({ data: rawMessage, injectConversation: false })
          if (message.isUnread && !_savedFirstUnreadMessage) {
            conversation._savedFirstUnreadMessage = message

            if (message.unreadMessageCount) {
              conversation.unreadCount = message.unreadMessageCount
            }

            if (message.unreadPriorityMessageCount) {
              conversation.unreadPriorityCount = message.unreadPriorityMessageCount
            }
          }

          message.inTimeline = true
          if (!message.isOutgoing) {
            const attrs = {
              id: `${message.id}:${message.counterPartyId}`,
              messageId: message.id,
              status: MessageRecipientStatus.resolve(rawMessage.status),
              userId: this.host.currentUserId,
            }
            this.host.models.MessageStatusPerRecipient.inject(attrs)
          }
          message.senderStatus = MessageSenderStatus.SENT
          this.host.models.Message.inject(message)
        } else {
          this.logger.warn(`Skipping timeline entry with unrecognized xmlns: ${xmlns}`)
        }
      }
      if (direction === 'Higher' || direction === 'Initial') {
        let itemsEstimate = timeline.length === 0 ? 0 : 1
        if (anchorPoint === ConversationAnchorPoints.CONVERSATION_END) {
          itemsEstimate = 0
        }

        if (itemsEstimate === 1 && direction === 'Initial') {
          if (timeline.length < maxItems) {
            itemsEstimate = 0
          } else if (timeline.length === maxItems) {
            const lastTimelineItem = timeline[timeline.length - 1]
            if (conversation.lastMessage && lastTimelineItem.message_id === conversation.lastMessage.id) {
              itemsEstimate = conversation.lastMessage.isUnread ? 1 : 0
            }
          }
        }

        conversation.higherContinuation = {
          continuation: { higher_continuation },
          itemsEstimate,
        }
      }

      if (direction === 'Lower' || direction === 'Initial') {
        let itemsEstimate = timeline.length === 0 ? 0 : 1
        if (direction === 'Initial' && timeline.length < maxItems) {
          itemsEstimate = 0
        }

        conversation.lowerContinuation = {
          continuation: { lower_continuation },
          itemsEstimate,
        }
      }

      const { lowestSortNumber } = conversation
      if (anchorPoint !== ConversationAnchorPoints.CONTINUATION && lowestSortNumber !== undefined) {
        await this.markAsDelivered(conversation.id)
      }

      itemsAdded = timeline.length
      conversation.isLive = conversation.higherContinuation.itemsEstimate === 0
    }

    this.host.models.Conversation.inject(conversation)

    return itemsAdded
  }

  @reusePromise()
  async findAll(options={}) {
    options = _.defaults(options, { includeMutes: true })

    if (this.config.condensedReplays) {
      options.includeMutes = false
    }

    if (this._fetched) return this.getAll()

    let [ roster ] = await Promise.all([
      this.host.api.roster.findAll(),
      options.includeMutes ? this.host.mute.findAll() : Promise.resolve()
    ])

    if (this.config.condensedReplays) {
      return this._processFindAllCR(roster)
    } else {
      return this._processFindAll(roster)
    }
  }

  async findAllForOrganization(organizationId) {
    organizationId = this._resolveModelIdWithTypes(organizationId, 'organization')

    if (!this.config.condensedReplays) {
      throw new errors.NotSupportedError(
        'findAllForOrganization() may not be called if this.config.condensedReplays=false'
      )
    }

    if (this._fetched) return this.getAllForOrganization(organizationId)

    let roster = await this.host.api.roster.findAll({ organizationId })

    return this._processFindAllCR(roster)
  }

  _processFindAllCR(roster) {
    const nonUsers = []
    const users = []
    const ensureOptions = {
      organization: 'placeholder',
    }

    for (let origIndex = 0; origIndex < roster.length; origIndex++) {
      let rawConversation = roster[origIndex]
      const { entity } = rawConversation
      if (!entity) continue

      const entityType = this.host.modelNameByEntityType(entity.type)

      if (entityType === 'user') {
        users.push({ rawConversation, origIndex })
      } else if (entityType) {
        nonUsers.push({ rawConversation, origIndex })
      }
    }

    // users are a first priority to inject as they may be members in groups,
    // and injecting groups triggers ensures members. if the user exists before the ensure, group
    // wouldn't need to put a placeholder member

    for (const rosterEntry of users) {
      const { rawConversation, origIndex } = rosterEntry
      rosterEntry.conversation = this.__injectConversation(rawConversation, { origIndex })
    }

    for (const rosterEntry of nonUsers) {
      const { rawConversation, origIndex } = rosterEntry
      rosterEntry.conversation = this.__injectConversation(rawConversation, { origIndex })
    }

    const conversations = []

    while (nonUsers.length > 0 || users.length > 0) {
      const rosterEntry = nonUsers.length === 0 ? users.shift() :
        users.length === 0 ? nonUsers.shift() :
        nonUsers[0].origIndex < users[0].origIndex ? nonUsers.shift() : users.shift()

      const { conversation } = rosterEntry
      this.__ensureEntities(conversation, ensureOptions)
      conversations.push(conversation)
    }

    this._fetched = true

    return conversations
  }

  _processFindAll(roster) {
    const nonUsers = []
    const toProcess = []
    const users = []

    // first, loop through all entries and inject users
    // users are a first priority to inject as they may be members in groups,
    // and injecting groups triggers ensures members. if the user exists before the ensure, group
    // wouldn't need to put a placeholder member

    for (let origIndex = 0; origIndex < roster.length; origIndex++) {
      let rosterEntry = roster[origIndex]

      let {
        organization_key: organizationId,
        type,
        ...entityAttrs
      } = rosterEntry

      const entityType = this.host.modelNameByTypeNS(type)
      entityAttrs.organizationId = organizationId

      rosterEntry = {
        entityAttrs,
        entityType,
        organizationId,
        origIndex,
      }

      if (rosterEntry.entityType === 'user') {
        rosterEntry.counterParty = this.__injectCounterParty(rosterEntry)
        users.push(rosterEntry)
      } else if (rosterEntry.entityType) {
        if (entityType === 'group') {
          rosterEntry.groupType = this.host.groups.__extractGroupTypeFromAttrs(entityAttrs)
        }

        if (rosterEntry.groupType === GroupType.FORUM) {
          delete entityAttrs.members
        }

        toProcess.push(rosterEntry)
      }
    }

    for (const rosterEntry of toProcess) {
      rosterEntry.counterParty = this.__injectCounterParty({ ...rosterEntry, hasCurrentUser: true })
      nonUsers.push(rosterEntry)
    }

    const conversations = []

    while (nonUsers.length > 0 || users.length > 0) {
      const rosterEntry = nonUsers.length === 0 ? users.shift() :
        users.length === 0 ? nonUsers.shift() :
        nonUsers[0].origIndex < users[0].origIndex ? nonUsers.shift() : users.shift()

      const { conversationId, counterParty, lastMessage, muted, organizationId } = rosterEntry
      // TODO take care of all entities that are not full, not just here. maybe isFull property that
      // contains all the needed attributes
      delete counterParty.$placeholder

      let highestSortNumber
      if (this.host.config.condensedReplays && lastMessage) {
        highestSortNumber = lastMessage.sort_number
      }

      const conversation = this.ensureConversation(
        counterParty.$entityType,
        counterParty.id,
        organizationId,
        // on initial roster load, no need for all nested entities
        // users and shallow groups (not all members guaranteed) are already in
        {
          conversationId,
          ensureEntities: {
            organization: 'placeholder',
          },
          highestSortNumber,
          muted,
        },
      )

      conversation._counterPartyAddedOnServer = true
      conversations.push(conversation)
    }

    this._fetched = true

    return conversations
  }

  async find(id: string, options={}) {
    options = _.defaultsDeep(options, {
      ensureEntities: {
        counterParty: 'full',
        escalationExecution: 'full',
        groupMembers: 'placeholder',
        messageCounterParties: 'full',
        organization: 'placeholder',
        touchOnRelatedEntityLoad: false,
      }
    })
    let conversation

    if (this.config.condensedReplays) {
      let rawConversation = await this.host.api.conversations.find(id)

      if (!rawConversation) throw new errors.NotFoundError(this.host.models.Conversation.name, id)

      conversation = this.__injectConversation(rawConversation)

      if (!conversation) throw new errors.NotFoundError(this.host.models.Conversation.name, id)
    } else {
      conversation = this.getById(id)

      if (!conversation) {
        await this.findAll()
        conversation = this.getById(id)
      }
      if (!conversation) {
        throw new errors.NotFoundError(this.host.models.Conversation.name, id)
      }
    }
    await this.__ensureEntities(conversation, options.ensureEntities)

    return conversation
  }

  __injectConversation(rawConversation, {
    highestSortNumber,
    origIndex=this.host.models.Conversation.getAll().length,
  }={}) {
    let counterParty
    let {
      conversation_id: conversationId,
      entity: entityAttrs,
      first_expiring_unread_message: rawFirstExpiringUnreadMessage,
      in_progress_escalation_count: inProgressEscalationCount,
      last_message: rawLastMessage,
      lowest_sort_number: lowestSortNumber,
      muted,
      organization_id: organizationId,
      unread_message_count: unreadCount,
      unread_priority_message_count: unreadPriorityCount,
    } = rawConversation

    let conversation = this.getById(conversationId)
    if (conversation) {
      counterParty = conversation.counterParty
      organizationId = conversation.organizationId
    } else {
      if (!organizationId) {
        organizationId = rawConversation.organization_key
      }

      if (!muted && entityAttrs && entityAttrs.muted) {
        muted = entityAttrs.muted
        delete entityAttrs.muted
      }
      if (entityAttrs) {
        counterParty = this.__injectCounterParty({
          conversationId,
          entityAttrs,
          entityType: entityAttrs.$entityType,
          hasCurrentUser: true,
          organizationId,
        })
      }

      if (!counterParty) return null
      delete counterParty.$placeholder

      if (!conversation) {
        conversation = this.host.models.Conversation.createInstance({
          id: conversationId,
          counterParty,
          organizationId,
          _origIndex: origIndex,
        })
      }
    }

    const { id: counterPartyId, $entityType: counterPartyType } = counterParty

    if (rawFirstExpiringUnreadMessage) {
      if (rawLastMessage) {
        if (rawLastMessage.message_id === rawFirstExpiringUnreadMessage.message_id) {
          rawFirstExpiringUnreadMessage = rawLastMessage
        } else {
          rawFirstExpiringUnreadMessage.senderId = rawLastMessage.sender_token
          rawFirstExpiringUnreadMessage.senderOrganizationId = rawLastMessage.sender_organization
        }
      }

      conversation.firstExpiringUnreadMessage = this._ensureFirstExpiringUnreadMessage({
        rawFirstExpiringUnreadMessage, conversationId, counterParty,
      })
    }

    if (rawLastMessage) {
      let lastMessage = this._ensureLastMessage({
        rawLastMessage, conversationId, counterParty,
      })

      if (lastMessage && lastMessage.isEphemeral) {
        const { lastEphemeralMessage } = conversation

        if (!lastEphemeralMessage || lastMessage.sortNumber > lastEphemeralMessage.sortNumber) {
          conversation.lastEphemeralMessage = lastMessage
        }
      }

      // NOTE: Server does not return ephemeral messages for rawConversation.last_message
      // so we need to set it in case it is next message to show in the preview
      if (conversation.lastEphemeralMessage) {
        const { lastEphemeralMessage } = conversation

        if (!lastMessage || lastEphemeralMessage.sortNumber > lastMessage.sortNumber) {
          lastMessage = lastEphemeralMessage
        }
      }

      conversation.lastMessage = lastMessage

      if (highestSortNumber === undefined) {
        highestSortNumber = rawLastMessage.sort_number
      }
    }

    conversation._counterPartyAddedOnServer = true
    if (entityAttrs && entityAttrs.metadata) {
      conversation.metadata = entityAttrs.metadata
    }

    if (highestSortNumber !== undefined) conversation.highestSortNumber = highestSortNumber
    if (lowestSortNumber !== undefined && lowestSortNumber !== 0) conversation.lowestSortNumber = lowestSortNumber
    if (inProgressEscalationCount !== undefined) conversation.inProgressEscalationCount = inProgressEscalationCount
    if (muted !== undefined) conversation.muted = muted
    if (unreadCount !== undefined) conversation.unreadCount = unreadCount
    if (unreadPriorityCount !== undefined) conversation.unreadPriorityCount = unreadPriorityCount

    this.host.models.Conversation.inject(conversation)

    const conversationHandle = this.getConversationKey(counterPartyType, counterPartyId, organizationId)
    this._convByConvHandle[conversationHandle] = conversation

    return conversation
  }

  async refetch() {
    this._fetched = false
    return this.findAll()
  }

  _ensureLastMessage({ rawLastMessage, conversationId, counterParty }) {
    const { message_id: messageId, ...messageAttrs } = rawLastMessage
    if (messageId) {
      let message = this.host.messages.getById(messageId)

      if (message) {
        message.shouldEnsureRecipientStatus = true
        message.senderStatus = MessageSenderStatus.SENT
        this.host.models.Message.inject(message)
      } else {
        messageAttrs.senderStatus = MessageSenderStatus.SENT
        messageAttrs.id = messageId
        messageAttrs.shouldEnsureRecipientStatus = true
        messageAttrs.conversationId = conversationId
        messageAttrs.counterParty = counterParty

        message = this.host.models.Message.injectPlaceholder(messageAttrs)
      }

      return message
    }
    return null
  }

  _ensureFirstExpiringUnreadMessage({
    rawFirstExpiringUnreadMessage, conversationId, counterParty,
  }) {
    const { expire_in: expireIn, message_id: messageId, ...messageAttrs } = rawFirstExpiringUnreadMessage
    if (messageId) {
      let message = this.host.messages.getById(messageId)

      if (message) {
        message.shouldEnsureRecipientStatus = true
        message.expireIn = expireIn

        this.host.models.Message.inject(message)
      } else {
        messageAttrs.id = messageId
        messageAttrs.shouldEnsureRecipientStatus = true
        messageAttrs.expireIn = expireIn
        messageAttrs.conversationId = conversationId
        messageAttrs.counterParty = counterParty

        message = this.host.models.Message.injectPlaceholder(messageAttrs)
      }

      return message
    }

    return null
  }

  async markAsDelivered(conversationId: string|Object) {
    let conversation = this._resolveEntity(conversationId, 'conversation')
    if (!conversation) throw new errors.NotFoundError(this.host.models.Conversation.name, conversationId)
    conversationId = this._resolveModelId(conversationId)
    if (this.config.condensedReplays) {
      const { highestSortNumber, lowestSortNumber } = conversation
      try {
        const deliveredStatus = MessageRecipientStatus.toServer(MessageRecipientStatus.DELIVERED)

        await this.host.api.conversations.updateConversationStatus(
          conversationId,
          deliveredStatus,
          {
            highestSortNumber,
            lowestSortNumber,
          }
          )

        conversation.lowestSortNumber = null
      } catch (err) {
        this.logger.warn(`failed to mark conversation ${conversationId} as delivered`)
      }
      this.host.models.Conversation.inject(conversation)
    } else {
      return this.host.messages.markAsDelivered(conversation.messages)
    }
  }

  async markAsRead(conversationId: string|Object, options={}) {
    let conversation = this._resolveEntity(conversationId, 'conversation')
    if (!conversation) throw new errors.NotFoundError(this.host.models.Conversation.name, conversationId)
    conversationId = this._resolveModelId(conversationId)

    if (this.config.condensedReplays) {
      const { highestSortNumber, unreadCount } = conversation
      if (unreadCount === 0) return

      const numOfMessages = conversation.messages.length
      conversation._markingAsRead = true
      conversation._markingAsReadSortNumber = numOfMessages > 0 ? conversation.messages[numOfMessages - 1].sortNumber : 0
      conversation._markingAsReadExpiration = (new Date()).getTime() + 30*1000

      this.host.models.Conversation.inject(conversation)
      try {
        const readStatus = MessageRecipientStatus.toServer(MessageRecipientStatus.READ)

        await this.host.api.conversations.updateConversationStatus(conversationId, readStatus, { highestSortNumber })
      } catch (err) {
        this.logger.warn(`failed to mark conversation ${conversationId} as read`)
      }
    } else {
      return this.host.messages.markAsRead(conversation.markableAsReadMessages, options)
    }
  }

  // add/remove conversations to server roster
  @reusePromise()
  async add(counterPartyType: string, counterPartyId: string, organizationId: string) {
    const conversation = this.ensureConversation(counterPartyType, counterPartyId, organizationId)

    try {
      await this.host.api.roster.add(counterPartyType, counterPartyId, organizationId)
    } catch (err) {
      this.logger.warn(`can't add ${counterPartyId} ${counterPartyType}`, err)
    }

    conversation._counterPartyAddedOnServer = true
  }

  @reusePromise()
  async remove(conversationId: string|Object) {
    let conversation = this._resolveEntity(conversationId, 'conversation')
    if (!conversation) {
      throw new errors.NotFoundError(this.host.models.Conversation.name, conversationId)
    }
    const { counterPartyId, counterPartyType, organizationId } = conversation

    try {
      await this.host.api.roster.remove(counterPartyType, counterPartyId, organizationId)
    } catch (e) {
      this.logger.warn(`can't remove conversation ${conversationId}`)
    }
  }

  ensureConversation(counterPartyType: string, counterPartyId: string, organizationId: string, options = {}) {
    options = _.defaultsDeep(options, {
      ensureEntities: {
        counterParty: false, organization: false, messageCounterParties: false, groupMembers: false,
        touchOnRelatedEntityLoad: true
      },
    })

    let { conversationId } = options
    const conversationHandle = this.getConversationKey(counterPartyType, counterPartyId, organizationId)
    if (this.config.condensedReplays && !conversationId) {
      const conversation = this._convByConvHandle[conversationHandle]
      if (conversation) conversationId = conversation.id
    }
    if (!conversationId) {
      conversationId = conversationHandle
    }

    let conversation = this.getById(conversationId)
    if (conversation) {
      // fire and forget __ensureEntities. will touch conversation when any related entity is loaded
      this.__ensureEntities(conversation, options.ensureEntities)
      let changed = false

      if (this.config.condensedReplays && options.highestSortNumber !== undefined) {
        conversation.highestSortNumber = options.highestSortNumber
        changed = true
      }

      if (this.config.condensedReplays && options.muted !== undefined) {
        conversation.muted = options.muted
        changed = true
      }

      this._convByConvHandle[conversationHandle] = conversation

      if (changed) {
        this.host.models.Conversation.inject(conversation)
      }

      return conversation
    }

    const attrs = {
      id: conversationId,
      counterPartyType,
      counterPartyId,
      organizationId,
      _origIndex: this.host.models.Conversation.getAll().length,
    }

    if (options.highestSortNumber) attrs.highestSortNumber = options.highestSortNumber
    if (options.muted) attrs.muted = options.muted

    conversation = this.host.models.Conversation.createInstance(attrs)

    // fire and forget __ensureEntities. will touch conversation when any related entity is loaded
    this.__ensureEntities(conversation, options.ensureEntities)
    this.host.models.Conversation.inject(conversation)

    this._convByConvHandle[conversationHandle] = conversation

    return conversation
  }

  __ejectConversation(conversationId: string) {
    const conversation = this.getById(conversationId)

    if (conversation) {
      const { counterPartyType, counterPartyId, organizationId } = conversation
      const conversationHandle = this.getConversationKey(counterPartyType, counterPartyId, organizationId)
      delete this._convByConvHandle[conversationHandle]
      this.host.models.Conversation.eject(conversation)
      this.__queuedConversationsToReload = this.__queuedConversationsToReload
        .filter(({ id }) => id !== conversation.id)
    }

    return conversation
  }

  reactToMessageEvent({ data }) {
    if (this.config.condensedReplays) {
      this.reactToMessageEventCR({ data })
      return
    }

    const { message, conversation } = this._reactToMessageEvent({ data })
    this.host.models.Message.inject(message)
    this.emit('message', message)
    if (conversation) {
      this.__addConversationCounterPartyToServerRosterIfShould(conversation)
      conversation.emit('message', message)
      if (this.config.condensedReplays && conversation._markingAsReadSortNumber && message.sortNumber > conversation._markingAsReadSortNumber) {
        this.__clearOptimisticStatus([conversation])
      }
    }
    const isNotDelivered =
      MESSAGE_STATUS_ORDER[message.markedRecipientStatus] < MESSAGE_STATUS_ORDER[MessageRecipientStatus.DELIVERED]
    const options = {}

    if (isNotDelivered) {
      this.host.messages.markAsReceived(message, options)
    }
  }

  reactToMessageEventCR({ data }) {
    const options = {}

    const { message, conversation } = this._reactToMessageEventCR({ data })
    this.host.models.Message.inject(message)

    this.emit('message', message)

    if (conversation) {
      this.__addConversationCounterPartyToServerRosterIfShould(conversation)
      conversation.emit('message', message)
    }

    options.queue = true
    this.host.messages.markAsReceived(message, options)
  }

  _reactToMessageEvent({ data, conversationId=null }) {
    data = jsonCloneDeep(data)

    const currentUserId = this.host.currentUserId

    let recipientStatuses = data['recipient_status']
    if (!recipientStatuses) recipientStatuses = []
    const p2pMessageStatus = {
      status: data['status'],
      timestamp: data['created_time'],
    }

    const attrs = this.host.models.Message.parseAttrs(data)

    attrs.messageType = MessageType.USER_SENT

    let hasCounterParty = true
    if (attrs.groupId) {
      attrs.counterPartyType = 'group'
      attrs.counterPartyId = attrs.groupId
    } else if (attrs.recipientId) {
      if (this.host.distributionLists.getById(attrs.recipientId)) {
        attrs.counterPartyType = 'distributionList'
      } else if (this.host.users.getById(attrs.recipientId)) {
        attrs.counterPartyType = 'user'
      } else {
        hasCounterParty = false
        const distributionListName = this.host.messages._looksLikeDistributionListMessage(attrs)
        if (distributionListName) {
          attrs.counterPartyType = 'distributionList'
        } else {
          attrs.counterPartyType = 'user'
        }
      }

      attrs.counterPartyId = (
        attrs.counterPartyType === 'distributionList' || attrs.senderId === currentUserId
      ) ? attrs.recipientId : attrs.senderId
    }

    const isOutgoing = this.host.messages._isMessageOutgoing(attrs)

    if (attrs.counterPartyType !== 'distributionList') {
      p2pMessageStatus['account_id'] = isOutgoing ?
        attrs.recipientId : this.host.currentUserId
      if (p2pMessageStatus['account_id']) recipientStatuses.push(p2pMessageStatus)
    }
    attrs.senderStatus = MessageSenderStatus.SENT

    const conversation = hasCounterParty && this.ensureConversation(
      attrs.counterPartyType, attrs.counterPartyId, attrs.recipientOrganizationId, {
        conversationId,
        ensureEntities: {
          counterParty: 'placeholder', groupMembers: 'placeholder', organization: 'placeholder',
        }
      }
    )
    attrs.conversationId = conversation ? conversation.id : null

    let message = this.host.models.Message.get(attrs.id)

    if (message) {
      const {
        body,
        escalationExecutionId,
        priority,
        sortNumber,
        sortSeries,
      } = attrs

      message.body = body
      message.escalationExecutionId = escalationExecutionId || null
      message.priority = priority
      message.shouldEscalate = false
      message.sortNumber = sortNumber
      message.sortSeries = sortSeries
    } else {
      message = this.host.models.Message.createInstance(attrs, false)
    }

    const otherUserIdInMessage = message.senderId === currentUserId ?
      message.recipientId : message.senderId
    if (otherUserIdInMessage && message.counterPartyType !== 'distributionList') {
      this.__ensureMessageCounterParty(conversation, otherUserIdInMessage, {
        messageCounterParties: this.host.currentlyServingOfflineMessages ? 'placeholder' : 'full',
        touchOnRelatedEntityLoad: true
      })
    }

    for (const statusAttrs of recipientStatuses) {
      if (!this.config.condensedReplays) {
        this.host.messages.__injectMessageStatus(message, statusAttrs)
      }
    }

    if (
      attrs.counterPartyType === 'group' || // server does not return a status for every group recipient, so we have to fetch them later
      this.config.condensedReplays
    ) {
      message.shouldEnsureRecipientStatus = true
    }

    message.inTimeline = !!(conversation && conversation.isLive)

    return { message, conversation }
  }

  _reactToMessageEventCR({ data={}, injectConversation=true }={}) {
    data = jsonCloneDeep(data)
    const currentUserId = this.host.currentUserId
    let messageData
    let highestSortNumber

    if (data.last_message) {
      messageData = data.last_message
      messageData.conversation_id = data.conversation_id
      highestSortNumber = data.last_message.sort_number
    } else {
      messageData = data
    }

    if (messageData['is_group'] && !messageData['group_token']) {
      messageData['group_token'] = messageData['recipient_token']
    }

    const attrs = this.host.models.Message.parseAttrs(messageData)

    attrs.messageType = MessageType.USER_SENT

    if (attrs.groupId) {
      attrs.counterPartyType = 'group'
      attrs.counterPartyId = attrs.groupId
    } else if (attrs.recipientId) {
      if (this.host.distributionLists.getById(attrs.recipientId)) {
        attrs.counterPartyType = 'distributionList'
      } else if (this.host.users.getById(attrs.recipientId)) {
        attrs.counterPartyType = 'user'
      } else {
        const distributionListName = this.host.messages._looksLikeDistributionListMessage(attrs)
        if (distributionListName) {
          attrs.counterPartyType = 'distributionList'
        } else {
          attrs.counterPartyType = 'user'
        }
      }

      attrs.counterPartyId = (
        attrs.counterPartyType === 'distributionList' || attrs.senderId === currentUserId
      ) ? attrs.recipientId : attrs.senderId
    }

    attrs.senderStatus = MessageSenderStatus.SENT

    let message = this.host.models.Message.get(attrs.id)

    if (message) {
      this.host.models.Message.inject({ shouldEscalate: false, ...attrs })
    } else {
      message = this.host.models.Message.inject(attrs)
    }

    let conversation
    if (injectConversation) {
      if (!data.entity) {
        data.entity = this._resolveEntity(attrs.counterPartyId, attrs.counterPartyType)
      }
      if (!data.organization_id) {
        if (data.sender_id) {
          message.isOutgoing = this.host.messages._isMessageOutgoing(message)
        }

        if (message.isOutgoing && message.senderOrganizationId) {
          message.__organizationId = message.senderOrganizationId
        } else if (!message.isOutgoing && message.recipientOrganizationId) {
          message.__organizationId = message.recipientOrganizationId
        }

        data.organization_id = message.__organizationId
      }

      conversation = this.__injectConversation(data, { highestSortNumber })
    } else {
      conversation = this.host.conversations.getById(messageData.conversation_id)
    }

    const otherUserIdInMessage = message.senderId === currentUserId ?
      message.recipientId : message.senderId
    const ensureOnlyForumGroups = message.counterPartyType !== 'group' || message.counterParty && message.counterParty.groupType === 'FORUM'
    if (otherUserIdInMessage && message.counterPartyType !== 'distributionList' && ensureOnlyForumGroups) {
      this.__ensureMessageCounterParty(conversation, otherUserIdInMessage, {
        messageCounterParties: this.host.currentlyServingOfflineMessages ? 'placeholder' : 'full',
        touchOnRelatedEntityLoad: true
      })
    }

    message.inTimeline = !!message.isEphemeral || !!(conversation && conversation.isLive)
    message.shouldEnsureRecipientStatus = message.inTimeline

    return { message, conversation }
  }

  reactToFriendsEventCR({ data, eventId }) {
    data = jsonCloneDeep(data)
    const { action, xmlns, ...rawConversation } = data
    const { entity: entityAttrs } = rawConversation
    const organizationId = rawConversation.organization_id || rawConversation.organization_key
    const { metadata } = entityAttrs
    const entityId = entityAttrs.id || entityAttrs.token
    if (!entityAttrs.type) return
    let entityType = this.host.modelNameByEntityType(entityAttrs.type)

    entityAttrs.organizationId = organizationId

    if (entityType === 'group' && metadata && (
      metadata['feature_service'] === 'role' && metadata['meta_type'] === 'role_assignment'
    )) {
      this._rolesFriendsEventPromise = this.host.roles.reactToFriendsEventCR({ action, attrs: entityAttrs, id: eventId })
      return
    }

    const {
      conversation_id: conversationId,
      last_message: rawLastMessage,
      lowest_sort_number: lowestSortNumber,
    } = rawConversation
    let conversation = this.getById(conversationId)

    if (action === 'add' || action === 'update') {
      if (conversation && action === 'update') {
        this.clearMessageStatuses(conversation)
      }

      let highestSortNumber = rawLastMessage ? rawLastMessage.sort_number : undefined
      if (highestSortNumber === undefined) {
        if (entityType === 'group') {
          highestSortNumber = this.host.messages.__getNextSortNumber(organizationId)
        } else {
          highestSortNumber = conversation && action === 'add' ? conversation.highestSortNumber : 1
        }
      }

      conversation = this.__injectConversation(rawConversation, { highestSortNumber })
      if (lowestSortNumber) {
        this.markAsDelivered(conversation)
      }

      if (action === 'add' && conversation) {
        const groupType = entityType === 'group' && this.host.groups.__extractGroupTypeFromAttrs(entityAttrs)
        if (groupType === GroupType.GROUP || groupType === GroupType.FORUM) {
          entityAttrs.members = null
        }

        this.__injectCounterParty({
          entityAttrs, entityType, groupType, hasCurrentUser: true, isPlaceholder: true, organizationId,
        })
        this.clearTimeline(conversation)
      }
    } else if (action === 'del') {
      this.__removeFriend({ attrs: entityAttrs, entityType, id: entityId, organizationId })
    } else {
      this.logger.error('friends action not implemented', action, data)
    }

    if (conversation) this.__clearOptimisticStatus([conversation])
    this.emit('friends', { action, ...entityAttrs })
  }

  reactToFriendsEvent({ data, id: eventId }) {
    if (this.config.condensedReplays) {
      this.reactToFriendsEventCR({ data, eventId })
      return
    }

    data = jsonCloneDeep(data)
    const { action, organization_id: organizationId, ...entityAttrs } = data['friend']
    const { metadata, token: id } = entityAttrs
    const entityType = this.host.modelNameByTypeNS(entityAttrs['xmlns'])
    if (!entityType) return

    if (entityType === 'group' && metadata && (
      metadata['feature_service'] === 'role' && metadata['meta_type'] === 'role_assignment'
    )) {
      this._rolesFriendsEventPromise = this.host.roles.reactToFriendsEvent({ data, id: eventId })
      return
    }

    if (action === 'add') {
      const groupType = entityType === 'group' && this.host.groups.__extractGroupTypeFromAttrs(entityAttrs)
      if (groupType === GroupType.GROUP || groupType === GroupType.FORUM) {
        entityAttrs.members = null
      }

      this.__injectCounterParty({
        entityAttrs, entityType, groupType, hasCurrentUser: true, isPlaceholder: true, organizationId,
      })

      const highestSortNumber = (
        entityType === 'group' ? this.host.messages.__getNextSortNumber(organizationId) : 1
      )
      this.ensureConversation(entityType, id, organizationId, { highestSortNumber })
    } else if (action === 'del') {
      this.__removeFriend({ attrs: entityAttrs, entityType, id, organizationId })
    } else {
      this.logger.error('friends action not implemented', action, data)
    }

    this.emit('friends', { action, ...entityAttrs })
  }

  __injectCounterParty = ({
    conversationId,
    entityAttrs,
    entityType,
    groupType,
    hasCurrentUser=false,
    isPlaceholder=false,
    organizationId,
  }) => {
    let { id, metadata } = entityAttrs
    if (!id) id = entityAttrs.token
    if (!entityType && entityAttrs.type) {
      entityType = this.host.modelNameByEntityType(entityAttrs.type)
    }
    if (conversationId) entityAttrs.conversationId = conversationId
    if (organizationId) entityAttrs.organizationId = organizationId
    if (entityType === 'group' && !groupType) {
      groupType = this.host.groups.__extractGroupTypeFromAttrs(entityAttrs)
    }

    if (metadata && id && organizationId) {
      this.host.metadata.__injectMetadata(id, organizationId, metadata)
    }

    if (entityType === 'group') {
      if (metadata) {
        if (metadata['created_by']) {
          const { role=null, user=null } = this.__injectCreatedByEntity(metadata, organizationId)
          if (role || user) {
            entityAttrs.createdByRole = role
            entityAttrs.createdByUser = user
          }
        } else if (entityAttrs['created_by']) {
          const user = this.host.models.User.injectPlaceholder({ id: entityAttrs['created_by'] })
          entityAttrs.createdByUser = user
        }
        if (metadata['target_id']) {
          const { role=null, user=null } = this.__injectTargetEntity(metadata, organizationId)
          if (role || user) {
            entityAttrs.targetRole = role
            entityAttrs.targetUser = user
          }
        }
        if (metadata['meta_type'] === 'role_assignment') {
          const roleName = entityAttrs.display_name || entityAttrs.name
          this.host.roles.__injectRoleAssignmentEntity(roleName, metadata, organizationId)
        }
      }

      if (groupType === GroupType.FORUM && hasCurrentUser) {
        entityAttrs.hasCurrentUser = true
      }
    } else if (entityType === 'user') {
      const autoForwardReceiverId = entityAttrs['dnd_auto_forward_receiver']
      if (autoForwardReceiverId && autoForwardReceiverId !== 'undefined') {
        this.host.models.User.injectPlaceholder({ id: autoForwardReceiverId })
      }
    }

    if (id) {
      if (isPlaceholder) {
        return this.host.injectPlaceholderModel(entityAttrs, entityType)
      } else {
        return this.host.injectModel(entityAttrs, entityType)
      }
    } else {
      return null
    }
  }

  __injectCreatedByEntity = (metadata, organizationId) => {
    const createdBy = {}

    if (metadata['created_by_tag_id'] !== undefined) {
      createdBy.role = this.host.roles.__injectRoleFromMetadata({
        metadata: {
          token: metadata['created_by'],
          display_name: metadata['created_by_display_name'],
          tag_color: metadata['created_by_tag_color'],
          tag_id: metadata['created_by_tag_id'],
          tag_name: metadata['created_by_tag_name'],
        },
        organizationId,
        replaceExisting: false,
      })
    } else {
      const user = this.host.models.User.get(metadata['created_by'])
      if (user) {
        createdBy.user = user
      } else {
        const injectArgs = { id: metadata['created_by'] }
        if ('created_by_display_name' in metadata) {
          injectArgs.displayName = metadata['created_by_display_name']
        }
        createdBy.user = this.host.models.User.injectPlaceholder(injectArgs)
      }
    }

    return createdBy
  }

  __injectTargetEntity = (metadata, organizationId) => {
    const target = {}

    if (metadata['target_tag_id'] !== undefined) {
      target.role = this.host.roles.__injectRoleFromMetadata({
        metadata: {
          display_name: metadata['target_display_name'],
          tag_color: metadata['target_tag_color'],
          tag_id: metadata['target_tag_id'],
          tag_name: metadata['target_tag_name'],
          token: metadata['target_id'],
        },
        organizationId,
        replaceExisting: false,
      })
    } else {
      const user = this.host.models.User.get(metadata['target_id'])
      if (user) {
        target.user = user
      } else {
        const injectArgs = { id: metadata['target_id'] }
        if ('target_display_name' in metadata) {
          injectArgs.displayName = metadata['target_display_name']
        }
        target.user = this.host.models.User.injectPlaceholder(injectArgs)
      }
    }

    return target
  }

  __removeFriend = ({ attrs, entityType, id, organizationId }) => {
    const conversation = this.get(entityType, id, organizationId)
    const group = entityType === 'group' ? this.host.models.Group.get(id) : null
    const isForum = !!(group && group.groupType === GroupType.FORUM)

    let content
    if (entityType === 'user' || this.config.condensedReplays) {
      content = conversation && conversation.content.slice()
    } else {
      content = conversation && conversation.messages.slice()
    }

    if (conversation && !this.config.condensedReplays) {
      this.__reloadConversation(conversation, { notify: false })
    }

    if (isForum) {
      attrs.hasCurrentUser = false
      attrs.members = null

      this.host.models.Group.inject(attrs)
    }

    if (!this.config.keepConversationsForAllForums || !isForum) {
      if (conversation) {
        this.__ejectConversation(conversation.id)
      }

      if (group && this.config.condensedReplays) {
        this.host.models.Group.eject(group)
      }
    }

    if (Array.isArray(content)) {
      for (const item of content) {
        this.host.ejectModel(item, item.$entityType)
      }
    }
  }

  reactToUpdateEventCR({ data }) {
    data = jsonCloneDeep(data)
    const { entity, organization_id: organizationId } = data
    const {
      metadata,
      token: groupId,
      type,
    } = entity

    entity.organizationId = organizationId

    this._sanitizeGroupAttrs(entity)

    const entityType = this.host.modelNameByEntityType(type)
    const groupType = this.host.groups.__extractGroupTypeFromAttrs(entity)

    if (metadata && organizationId && groupId && GROUP_TYPES_WITH_METADATA.includes(groupType)) {
      this.host.metadata.__injectMetadata(groupId, organizationId, metadata)
    }

    if (metadata && metadata.feature_service && metadata.feature_service === 'role') {
      if (entityType === 'group' && metadata['target_id']) {
        return this.__injectTargetEntity(metadata, organizationId)
      }
      this.host.roles.reactToUpdateEventCR({ entity })
    }

    this.host.injectModel(entity, type)
  }

  reactToUpdateEvent({ data }) {
    if (this.config.condensedReplays) {
      this.reactToUpdateEventCR({ data })
      return
    }
    data = jsonCloneDeep(data)
    const {
      metadata,
      organization_key: organizationId,
      token: groupId,
      type,
    } = data

    this._sanitizeGroupAttrs(data)

    const entityType = this.host.modelNameByTypeNS(type)
    const groupType = this.host.groups.__extractGroupTypeFromAttrs(data)

    if (metadata && organizationId && groupId && GROUP_TYPES_WITH_METADATA.includes(groupType)) {
      this.host.metadata.__injectMetadata(groupId, organizationId, metadata)
    }

    if (metadata && metadata.feature_service && metadata.feature_service === 'role') {
      if (entityType === 'group' && metadata['target_id']) {
        return this.__injectTargetEntity(metadata, organizationId)
      }
      this.host.roles.reactToUpdateEvent({ data })
    }

    const { ...entity } = data
    this.host.injectModel(entity, type)
  }

  _sanitizeGroupAttrs = attrs => {
    const { members } = attrs
    const groupType = this.host.groups.__extractGroupTypeFromAttrs(attrs)

    if (groupType === GroupType.FORUM) {
      if (members && members.length === 0) {
        // the server always returns `members: []` for forums; represent those as null (unfetched) instead
        delete attrs['members']
      }
    }
  }

  _onSendingMessage = (message: Object) => {
    let conversation
    if (this.config.condensedReplays) {
      conversation = message.conversation
    } else {
      conversation = this.ensureConversation(
        message.counterPartyType, message.counterPartyId, message.senderOrganizationId,
        {
          ensureEntities: {
            counterParty: 'full', organization: 'placeholder', messageCounterParties: 'full',
            groupMembers: 'full', touchOnRelatedEntityLoad: true,
          }
        }
      )
    }

    if (!conversation) return

    if (this.host.config.condensedReplays && conversation && message) {
      if (conversation.highestSortNumber < message.sortNumber) {
        conversation.highestSortNumber = message.sortNumber
      }
    }
    this.__addConversationCounterPartyToServerRosterIfShould(conversation)
  }

  _onSentMessage = (message: Object) => {
    const conversation = this.ensureConversation(
      message.counterPartyType, message.counterPartyId, message.senderOrganizationId
    )

    if (this.host.config.condensedReplays && conversation && message) {
      if (conversation.highestSortNumber < message.sortNumber) {
        conversation.highestSortNumber = message.sortNumber
      }
    }

    if (this.config.condensedReplays) {
      this.host.models.Conversation.inject(conversation)
    } else {
      this.__reloadConversation(conversation)
    }

    conversation.emit('message', message)
  }

  _onChangeMessage = (resource, message) => {
    if (!message.conversation) return

    this.__queueReload(message.conversation)
  }

  _onRemoveMessage = (resource, message) => {
    let { conversation } = message
    if (!conversation) return

    if (this.config.condensedReplays) {
      const {
        firstExpiringUnreadMessage,
        id: conversationId,
        lastMessage,
        timeline,
      } = conversation

      conversation = this.getById(conversationId)
      if (!conversation) return

      const isLastMessage = lastMessage === message
      const isFirstExpiringUnreadMessage = firstExpiringUnreadMessage === message
      const removingLastMessageInConversation = timeline.length === 1 && isLastMessage

      if (isLastMessage || isFirstExpiringUnreadMessage) {
        this.find(conversation.id)
      }

      if (removingLastMessageInConversation) {
        conversation.lastMessage = null
      }

      this.__updateTimeline(conversation)
    } else {
      this.__queueReload(conversation)
    }
  }

  _onRemoveGroup = (resource, group) => {
    const conversationId = !group.conversationId ? this.getConversationKey('group', group.id, group.organizationId) : group.conversationId
    this.__ejectConversation(conversationId)
  }

  _onChangeGroup = (resource, group) => {
    if (group.conversationId) {
      this.host.models.Conversation.touch(group.conversationId)
    }
  }

  _onChangeOrganization = (resource, organization) => {
    const { conversations } = organization
    const previouslyEnabledIdx = this.__escalationEnabledOrganizations.indexOf(organization)
    const previouslyEnabled = previouslyEnabledIdx > -1

    if (previouslyEnabled !== organization.canEscalateMessages) {
      if (previouslyEnabled) {
        this.__escalationEnabledOrganizations.splice(previouslyEnabledIdx, 1)
      } else {
        this.__escalationEnabledOrganizations.push(organization)
      }

      if (conversations) {
        for (const conversation of conversations) {
          this.__queueReload(conversation)
        }
      }
    }
  }

  __queueReload(conversation) {
    if (!this.__queuedConversationsToReload.includes(conversation)) {
      this.__queuedConversationsToReload.push(conversation)
    }
    this.__flushConversationsToReload()
  }

  __flushConversationsToReload = () => {
    if (this.host.currentlyServingOfflineMessages) return

    if (this.__queuedConversationsToReload.length > 0) {
      const changedOrganizations = []
      this.host.organizations._deferConversationChanges = true

      for (let conversation of this.__queuedConversationsToReload) {
        const { organizationId } = conversation
        this.__reloadConversation(conversation)

        if (!changedOrganizations.includes(organizationId)) {
          changedOrganizations.push(organizationId)
        }
      }

      this.host.organizations._deferConversationChanges = false

      for (const organizationId of changedOrganizations) {
        const organization = this.host.organizations.getById(organizationId)
        if (organization) this.host.organizations.__reloadConversations(organization)
      }

      this.__queuedConversationsToReload.length = 0
    }
  }

  hasPendingReloads() {
    return this.__queuedConversationsToReload && this.__queuedConversationsToReload.length > 0
  }

  __updateTimeline(conversation) {
    const { content, timeline } = conversation
    timeline.length = 0

    for (const message of content) {
      if (message.inTimeline) timeline.push(message)
    }

    this.host.models.Conversation.inject(conversation)
  }

  __reloadConversation(conversation, { notify=true }={}) {
    const {
      _unreadMessages: unreadMessages,
      _unreadPriorityMessages: unreadPriorityMessages,
      content,
      higherContinuation,
      lowerContinuation,
      markableAsReadMessages,
      messages,
      timeline,
    } = conversation

    if (this.config.condensedReplays) {
      conversation.firstUnreadMessage = null
    } else {
      conversation.firstUnreadMessage = conversation._savedFirstUnreadMessage
    }

    let inProgressEscalationCount = 0
    let firstInTimeline = null
    let lastInTimeline = null
    markableAsReadMessages.length = 0
    messages.length = 0
    timeline.length = 0
    unreadMessages.length = 0
    unreadPriorityMessages.length = 0

    let index = -1
    for (const message of content) {
      index++
      const {
        escalationExecution,
        inTimeline,
        isOutgoing,
        isUnread,
        markedRecipientStatus,
        messageType,
        priority,
      } = message

      if (inTimeline) {
        if (firstInTimeline === null) {
          firstInTimeline = index
        }
        lastInTimeline = index
        timeline.push(message)
      }

      if (messageType !== MessageType.USER_SENT) {
        if (BANG_MESSAGE_TYPES.includes(messageType)) {
          conversation.lastBangMessageSortNumber = message.sortNumber
        }

        continue
      }

      messages.push(message)

      if (escalationExecution && escalationExecution.status === 'IN_PROGRESS' && !this.config.condensedReplays) {
        inProgressEscalationCount++
      }
      if (!this.config.condensedReplays && NEW_OR_DELIVERED.includes(markedRecipientStatus)) {
        markableAsReadMessages.push(message)
      }
      if (!isOutgoing) {
        if (isUnread) {
          if (!conversation.firstUnreadMessage) {
            conversation.firstUnreadMessage = message
          }
          unreadMessages.push(message)

          if (priority === MessagePriority.HIGH) {
            unreadPriorityMessages.push(message)
          }
        }
        conversation.lastIncomingMessageSortNumber = message.sortNumber
      }
    }

    if (!this.config.condensedReplays) {
      lowerContinuation.itemsEstimate = firstInTimeline
      higherContinuation.itemsEstimate = lastInTimeline
      if (lastInTimeline !== null) {
        higherContinuation.itemsEstimate = content.length - 1 - lastInTimeline
      }
    }

    conversation.isLive = higherContinuation.itemsEstimate === 0 || content.length === 0

    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null

    const lastConversationEntry = lastMessage || _.last(
      content.filter(message => (
        message.messageType === MessageType.GROUP_MEMBERSHIP_CHANGE &&
        message.groupMembersChange && message.groupMembersChange.action === 'ADD'
      ))
    )

    if (this.host.config.condensedReplays) {
      conversation.highestSortNumber = conversation.highestSortNumber || 0
    } else {
      conversation.inProgressEscalationCount = inProgressEscalationCount
      conversation.lastMessage = lastMessage
      conversation.unreadCount = unreadMessages.length
      conversation.unreadPriorityCount = unreadPriorityMessages.length

      conversation.highestSortNumber = (
        (lastConversationEntry && lastConversationEntry.sortNumber) ||
        conversation.lastConversationSortNumber || 0
      )
    }

    if (notify) {
      const { counterPartyType, counterPartyId, organizationId } = conversation
      const conversationHandle = this.getConversationKey(counterPartyType, counterPartyId, organizationId)
      this._convByConvHandle[conversationHandle] = conversation

      this.host.models.Conversation.inject(conversation)
    }
  }

  __addConversationCounterPartyToServerRosterIfShould(conversation: Object) {
    if (!conversation) return
    if (conversation._counterPartyAddedOnServer) return

    const { organization } = conversation
    if (!organization || organization.$placeholder) return

    this.add(conversation.counterPartyType, conversation.counterPartyId, conversation.organizationId)
  }

  addMembershipChangeLog(event) {
    const {
      action,
      actionTime,
      actor,
      alwaysAddToTimeline,
      createdAt,
      group,
      id: eventId,
      memberIds,
      members,
      sortNumber,
    } = event
    const { id: actorId } = actor
    const { id: groupId, organizationId } = group
    const { currentUser } = this.host
    const conversation = this.get('group', groupId, organizationId)
    let conversationId, inTimeline

    if (this.config.condensedReplays) {
      if (!conversation) return
      conversationId = conversation.id
    } else {
      conversationId = this.getConversationKey('group', groupId, organizationId)
    }

    if (alwaysAddToTimeline) {
      inTimeline = true
    } else {
      inTimeline = conversation ? conversation.isLive : false
    }

    const groupMembersChange = this.host.models.GroupMembersChange.inject({
      id: `groupMembersChange:${eventId}`,
      action,
      actionTime,
      actor,
      memberIds,
      members,
    })

    this.host.models.Message.inject({
      body: this.__generateGroupMembershipChangeBody(event),
      conversationId,
      counterPartyId: groupId,
      counterPartyType: 'group',
      createdAt,
      groupId,
      groupMembersChange,
      id: `bang:${eventId}`,
      inTimeline,
      isOutgoing: false,
      messageType: MessageType.GROUP_MEMBERSHIP_CHANGE,
      recipient: currentUser,
      senderId: actorId,
      sortNumber,
    })

    if (action === 'OPT_IN' || action === 'OPT_OUT') {
      this.host.roles.refresh(organizationId)
    }

    if (conversation && !this.config.condensedReplays) {
      this.__reloadConversation(conversation)
    }
  }

  __generateGroupMembershipChangeBody(event) {
    let { action, actor, group, members } = event
    const { groupType } = group

    const actionText = GROUP_MEMBERSHIP_ACTION_TEXT[action] || ROLE_ACTION_TEXT[action]
    const actorNameText = actor.id === this.host.currentUserId ? 'You' : actor.displayName
    const preposition = GROUP_MEMBERSHIP_ACTION_PREPOSITION[action] || ''

    let memberNameText = null

    if (action in GROUP_MEMBERSHIP_ACTION_PREPOSITION || action in ROLE_ACTION_TEXT) {
      memberNameText = members.map((user, i) => [
        user.id === this.host.currentUserId ? 'You' : user.displayName,
        (i === members.length - 2 ? ' and' : ',') + ' ',
      ])::_flatten()::_initial().join('')
    }

    if (action in ROLE_ACTION_TEXT) {
      return [
        memberNameText,
        actionText,
        actorNameText,
      ].filter(Boolean).join(' ')
    } else {
      return [
        actorNameText,
        actionText,
        memberNameText,
        preposition,
        'this',
        GROUP_TYPE_LABEL[groupType]
      ].filter(Boolean).join(' ')
    }
  }

  addEscalationExecutionChangeLog(event) {
    const {
      action,
      actionTime,
      actor,
      alwaysAddToTimeline,
      createdAt,
      escalationExecution,
      group,
      members,
      id: eventId,
      sortNumber,
    } = event

    const { id: groupId, organizationId } = group
    const { currentUser } = this.host
    const conversation = this.get('group', groupId, organizationId)
    let conversationId = conversation ? conversation.id : null
    let inTimeline

    if (!conversation) {
      if (this.config.condensedReplays) {
        return
      } else {
        conversationId = this.getConversationKey('group', groupId, organizationId)
      }
    }

    if (alwaysAddToTimeline) {
      inTimeline = true
    } else {
      inTimeline = conversation ? conversation.isLive : false
    }

    const escalationExecutionChange = this.host.models.EscalationExecutionChange.inject({
      id: `escalationExecutionChange:${eventId}`,
      action,
      actionTime,
      actor,
      members,
    })

    this.host.models.Message.inject({
      body: this.__generateGroupEscalationChangeBody(event, escalationExecution),
      conversationId,
      counterPartyId: groupId,
      counterPartyType: 'group',
      createdAt,
      escalationExecutionChange,
      escalationExecutionId: escalationExecution ? escalationExecution.id : null,
      groupId,
      id: `bang:${eventId}`,
      inTimeline,
      isOutgoing: false,
      messageType: MessageType.ESCALATION_EXECUTION_CHANGE,
      recipient: currentUser,
      senderId: actor ? actor.id : null,
      sortNumber,
    })

    if (conversation && !this.config.condensedReplays) {
      this.__reloadConversation(conversation)
    }
  }

  __generateGroupEscalationChangeBody(event, escalationExecution) {
    const { action, actor, members } = event
    const actionText = ESCALATION_ACTION_TEXT[action]

    let displayId = (escalationExecution && escalationExecution.displayId) || ''
    if (displayId.startsWith('#')) {
      displayId = displayId.slice(1, displayId.length)
    }
    if (displayId !== '') displayId = `${displayId} `

    if (action === EscalationChangeAction.MEMBER_ADDED) {
      return `${actionText} ${members[0].displayName} to this escalation`
    } else if (action !== EscalationChangeAction.INITIATED && actor && actor.displayName) {
      return `Escalation ${displayId}${actionText} by ${actor.displayName}`
    } else {
      return `Escalation ${displayId}${actionText}`
    }
  }

  ////////////
  /// hashes
  ////////////

  getMessageConversationKey(message: Object) {
    if (message.groupId) {
      return this.conversationHashForListEntityId(message.groupId)
    }
    if (message.distributionListId) {
      return this.conversationHashForListEntityId(message.distributionListId)
    }

    const counterPartyId = message.senderId === this.host.currentUserId ?
      message.recipientId :
      message.senderId

    return this.conversationHashForUserMessage(
      counterPartyId, message.senderOrganizationId, message.recipientOrganizationId
    )
  }

  getConversationKey(
    counterPartyType: string,
    counterPartyId: string,
    senderOrganizationId: ?string,
    recipientOrganizationId: ?string=senderOrganizationId,
  ) {
    switch (counterPartyType) {
      case 'user':
        return this.conversationHashForUserMessage(counterPartyId, senderOrganizationId, recipientOrganizationId)
      case 'group':
      case 'distributionList':
        return this.conversationHashForListEntityId(counterPartyId)
      default:
        throw new Error(`type ${counterPartyType} not supported in conversation`)
    }
  }

  conversationHashForUserMessage(
    counterPartyId: string,
    senderOrganizationId: string,
    recipientOrganizationId: ?string=senderOrganizationId,
  ) {
    const str = [counterPartyId, senderOrganizationId, recipientOrganizationId].join(':')
    return md5.b64(str)
  }

  conversationHashForListEntityId(entityId: string) {
    return md5.b64(entityId)
  }

  // aliases
  conversationHashForGroupId(...args) { return this.conversationHashForListEntityId(...args) }
  conversationHashForDistributionListId(...args) { return this.conversationHashForListEntityId(...args) }

  looksLikeConversationHandle(id: string) {
    return /^[a-zA-Z0-9\/+=]{24}$/.test(id)
  }

  ////////////
  /// finders and getters
  ////////////

  async findConversationWithUser(userId: string, organizationId: string) {
    return this.get(this.host.models.User.name, userId, organizationId)
  }

  async findConversationWithGroup(groupId: string, organizationId: string) {
    return this.get(this.host.models.Group.name, groupId, organizationId)
  }

  async findAllGroupConversationsWithMembers(
    memberIds: string|string[]|Object[],
    organizationId: string,
    {
      exact=true,
      includeCurrentUser=true,
      includeForums=true,
      includeRoles=true,
      senderId=null,
    }={}
  ) {
    memberIds = await this.resolveMembers(arrayWrap(memberIds))
    memberIds = memberIds.map(this._resolveModelId)

    const groups = await this.host.groups.findAllWithSpecificMembers(
      memberIds, organizationId, { exact, includeCurrentUser, includeForums, includeRoles, senderId }
    )

    const conversations = []
    for (const group of groups) {
      const conversation = this.get(this.host.models.Group.name, group.id, organizationId)
      if (conversation) conversations.push(conversation)
    }

    return conversations
  }

  findLocalRoleP2PConversation(recipientId, senderId) {
    let conversationResult
    let roleP2PConversations

    recipientId = this.host.roles.__resolveRoleId(recipientId)
    senderId = this.host.roles.__resolveRoleId(senderId)

    const recipientUser = this.host.models.User.get(recipientId)
    const senderUser = this.host.models.User.get(senderId)

    if (senderUser && senderUser.isRoleBot && senderUser.botRole) {
      roleP2PConversations = this.host.roles._roleP2PConversations[senderId]
    } else if (recipientUser && recipientUser.isRoleBot && recipientUser.botRole) {
      roleP2PConversations = this.host.roles._roleP2PConversations[recipientId]
    } else {
      return null
    }

    if (roleP2PConversations) {
      for (const conversation of roleP2PConversations) {
        const { $lastModified, counterParty } = conversation
        const { p2pRecipient, p2pSender } = counterParty
        if (!p2pRecipient || !p2pSender) continue

        const p2pRecipientId = this.host.roles.__resolveRoleId(p2pRecipient.id)
        const p2pSenderId = this.host.roles.__resolveRoleId(p2pSender.id)
        const matchesNormal = p2pRecipientId === recipientId && p2pSenderId === senderId
        const matchesReverse = p2pRecipientId === senderId && p2pSenderId === recipientId

        if (
          (matchesNormal || matchesReverse) &&
          (!conversationResult || conversationResult.$lastModified < $lastModified)
        ) {
          conversationResult = conversation
        }
      }
    }

    return conversationResult
  }

  async resolveMembers(memberIds) {
    return await Promise.all(memberIds.map(async memberId => {
      if (isEmail(memberId) || isPhone(memberId)) {
        try {
          const user = await this.host.users.find(memberId)

          return user.id
        } catch (err) {
          return memberId
        }
      }

      return memberId
    }))
  }

  async selectConversation(id: string, {
    anchorPoint=ConversationAnchorPoints.CONVERSATION_END,
    markAsDelivered=true,
    markAsRead=true,
    minItemsToFetch=DEFAULT_SELECT_CONVERSATION_ITEMS,
  }) {
    if (anchorPoint === ConversationAnchorPoints.CONTINUATION) {
      throw new Error('Cannot call selectConversation() with anchorPoint=\'CONTINUATION\'')
    }

    let conversation = this.getById(id)
    if (!conversation) return

    let addedMessages = 0

    addedMessages += await this.fetchTimeline(id, {
      anchorPoint,
      markAsDelivered,
      maxItems: minItemsToFetch,
    })

    let previousFirstUnreadMessage

    if (!this.config.condensedReplays) {
      if (conversation.markableAsReadMessages.length > 0) {
        conversation._savedFirstUnreadMessage = conversation.markableAsReadMessages[0]
      } else {
        conversation._savedFirstUnreadMessage = null
      }
      previousFirstUnreadMessage = conversation._savedFirstUnreadMessage
      conversation.firstUnreadMessage = conversation._savedFirstUnreadMessage
    } else {
      previousFirstUnreadMessage = conversation.firstUnreadMessage
    }

    if (markAsRead && conversation.unreadCount > 0) this.markAsRead(id, { localOnly: true })

    if (addedMessages > 0) {
      addedMessages = await this._fetchContinuations(id, { addedMessages, markAsDelivered, minItemsToFetch })
    }

    if (this.config.condensedReplays) {
      if (markAsRead && conversation.unreadCount > 0) this.markAsRead(id)
      this.__ensureEntities(conversation, { messageCounterParties: 'full' })
    } else {
      if (markAsRead) this.markAsRead(id)
    }

    return {
      addedMessages,
      previousFirstUnreadMessage,
    }
  }

  async _fetchContinuations(id, {
    addedMessages=0,
    markAsDelivered=true,
    minItemsToFetch=DEFAULT_SELECT_CONVERSATION_ITEMS
    }= {}) {
    const conversation = this.getById(id)
    if (!conversation) return

    while (addedMessages < minItemsToFetch) {
      const added = await this.fetchTimeline(id, {
        anchorPoint: ConversationAnchorPoints.CONTINUATION,
        markAsDelivered,
        continuation: conversation.higherContinuation.continuation,
      })

      addedMessages += added
      if (added === 0) break
    }

    while (addedMessages < minItemsToFetch) {
      const added = await this.fetchTimeline(id, {
        anchorPoint: ConversationAnchorPoints.CONTINUATION,
        markAsDelivered,
        continuation: conversation.lowerContinuation.continuation,
      })

      addedMessages += added
      if (added === 0) break
    }

    return addedMessages
  }

  getWithUser(
    userId: string|Object,
    organizationId: string|Object,
    { createIfNotFound=false }/*: { createIfNotFound?: ?boolean }*/={}
  ) {
    let conversation

    if (this.config.condensedReplays) {
      const user = this.host.users.getById(userId)
      if (user && user.profileByOrganizationId && user.profileByOrganizationId[organizationId]) {
        const conversationId = user.profileByOrganizationId[organizationId].conversationId
        if (conversationId) {
          conversation = this.getById(conversationId)
        }
      }
    } else {
      userId = this._resolveModelId(userId)
      organizationId = this._resolveModelId(organizationId)
      const key = this.conversationHashForUserMessage(userId, organizationId)

      conversation = this.getById(key)
    }

    if (!conversation && createIfNotFound) {
      conversation = this.ensureConversation('user', userId, organizationId)
    }

    return conversation || null
  }

  getWithListEntity(
    entityType: string,
    entityId: string|Object,
    { createIfNotFound=false, organizationId, conversationId=null }={ createIfNotFound: false }
  ) {
    let conversation
    if (this.config.condensedReplays) {
      if (conversationId) {
        conversation = this.getById(conversationId)
      }
    } else {
      entityId = this._resolveModelId(entityId)
      const key = this.conversationHashForListEntityId(entityId)
      conversation = this.getById(key)
    }

    if (!conversation && createIfNotFound) {
      const entity = this._resolveEntity(entityId, entityType)
      if (entity) {
        organizationId = entity.organizationId
      } else {
        throw new Error('Entity required when using createIfNotFound')
      }
      conversation = this.ensureConversation(entityType, entityId, organizationId, {conversationId})
    }

    return conversation
  }

  getWithGroup(groupId) {
    return this.getWithListEntity('group', groupId)
  }

  getWithDistributionList(distributionListId) {
    return this.getWithListEntity('distributionList', distributionListId)
  }

  get(counterPartyType: string, counterPartyId: string, organizationId: string) {
    let conversation
    const conversationHandle = this.getConversationKey(counterPartyType, counterPartyId, organizationId)

    if (this.config.condensedReplays) {
      conversation = this._convByConvHandle[conversationHandle]
    } else {
      conversation = this.host.models.Conversation.get(conversationHandle)
    }

    return conversation || null
  }

  getAll() { return this.host.models.Conversation.getAll() }
  getById(id: string) { return this.host.models.Conversation.get(id) }
  getMultiById(ids: Array<string>) { return this.host.models.Conversation.getMulti(ids) }

  getAllForOrganization(organizationId) {
    const organization = this._resolveEntity(organizationId, 'organization')
    if (!organization) return null

    return organization.conversations
  }

  // ensures all related entities are loaded/placeholders
  __ensureEntities(conversation: Object, options={}) {
    let promises = []
    let counterPartyPromise = Promise.resolve()

    if (options.counterParty) {
      counterPartyPromise = this.__ensureCounterParty(conversation, options)
      promises.push(counterPartyPromise)
    }
    if (options.escalationExecution) {
      promises.push(this.__ensureEscalationExecution(conversation, counterPartyPromise, options))
    }
    if (options.messageCounterParties) {
      promises.push(this.__ensureMessageCounterParties(conversation, options))
    }
    if (options.organization) {
      promises.push(this.__ensureOrganization(conversation, options))
    }

    promises = promises.filter(Boolean)

    return promises.length > 0 ?
      Promise.all(promises)
        .then(() => {
          options.onLoad && options.onLoad()
          this.host.models.Conversation.inject(conversation)
        })
        .catch(err => console.error(err)) :
      Promise.resolve()
  }

  __ensureOrganization(conversation: Object, options={}) {
    if (!this.host.models.Organization.shouldEnsure(conversation.organization, options.organization !== 'full')) {
      return null
    }

    return new Promise((resolve, reject) => {
      this.host.models.Organization.ensureEntity(conversation.organizationId, {
        onlyPlaceholder: options.organization === 'placeholder',

        onMissing: () => {
          this.logger.info(`org ${conversation.organizationId} not found, created placeholder org`)
          conversation.$loadingOrganization = true
        },

        onLoad: () => {
          this.logger.info(`org ${conversation.organizationId} loaded`)
          conversation.$loadingOrganization = false
          options.touchOnRelatedEntityLoad && this.host.models.Conversation.inject(conversation)
        },

        onFinish: resolve
      })
    })
  }

  async __ensureCounterParty(conversation: Object, options={}) {
    options = {...options}
    if (
      conversation.counterPartyType === 'group' &&
      conversation.counterParty && conversation.counterParty.groupType === GroupType.ROLE_P2P
    ) {
      options.counterParty = 'full'
      options.groupMembers = 'full'
    }

    const model = this.host.modelsByEntityType[conversation.counterPartyType]

    // do we really need to ensure this counter party?
    // in case of groups - check also if we need full members or not
    const shouldEnsure = model.shouldEnsure(conversation.counterParty, options.counterParty !== 'full', {
      groupMembersOnlyPlaceholder: options.groupMembers !== 'full'
    })
    if (!shouldEnsure) return null

    let counterPartyAttrs = {}

    switch (conversation.counterPartyType) {
      case 'user':
        break
      case 'group':
      case 'distributionList':
        counterPartyAttrs.organizationId = conversation.organizationId
        break
      default:
        throw new Error('unsupported counterPartyType', conversation.counterPartyType)
    }

    const groupEnsureMembers = (foundEntity) => {
      // special case for groups - need to load members too
      if (model.name === 'group' && options.counterParty !== 'placeholder' && options.groupMembers) {
        return this.host.groups.ensureMembers(foundEntity, {
          onlyPlaceholder: options.groupMembers === 'placeholder'
        })
      }
    }

    return new Promise((resolve, reject) => {
      model.ensureEntity(conversation.counterPartyId, {
        attrs: counterPartyAttrs,

        onlyPlaceholder: options.counterParty === 'placeholder',

        onMissing: () => {
          this.logger.info(
            `entry ${conversation.counterPartyId} not found, created placeholder` +
            ` on org ${conversation.organizationId}`
          )
          conversation.$loadingCounterParty = true
        },

        onLoad: () => {
          conversation.$loadingCounterParty = false
          if (options.touchOnRelatedEntityLoad) this.host.models.Conversation.inject(conversation)
        },

        onFinish: async (foundEntity) => {
          if (foundEntity) await groupEnsureMembers(foundEntity)
          resolve()
        }
      })
    })
  }

  async __ensureEscalationExecution(conversation: Object, counterPartyPromise, options={}) {
    await counterPartyPromise

    const { counterPartyType, counterParty } = conversation
    if (!counterParty || counterPartyType !== 'group') return

    const { escalationExecution, groupType } = counterParty
    if (groupType !== GroupType.ESCALATION || !escalationExecution) return

    await this.host.escalations.ensureEscalationExecution(escalationExecution)
  }

  __ensureMessageCounterParties(conversation: Object, options={}) {
    const allSenderIds = conversation.messages
      ::_map('senderId')
      .filter(Boolean)
      ::_uniq()
      ::_without(this.host.currentUserId, conversation.counterPartyId)

    return Promise.all(allSenderIds.map(
      senderId => this.__ensureMessageCounterParty(conversation, senderId, options)
    ))
  }

  __ensureMessageCounterParty(conversation, messageOtherUserId: string, options={}) {
    const sender = this.host.models.User.get(messageOtherUserId)

    if (!this.host.models.User.shouldEnsure(sender, options.messageCounterParties !== 'full')) return null

    return new Promise((resolve, reject) => {
      this.host.models.User.ensureEntity(messageOtherUserId, {
        onlyPlaceholder: options.messageCounterParties === 'placeholder',

        onMissing: () => {
          const msg = conversation ? (
            `entry ${conversation.counterPartyId} not found, ` +
            `created placeholder on org ${conversation.organizationId}`
          ) : `counterParty ${messageOtherUserId} not found`

          this.logger.info(msg)
        },

        onLoad: () => {
          if (conversation && options.touchOnRelatedEntityLoad) {
            this.host.models.Conversation.inject(conversation)
          }
        },

        onFinish: resolve,
      })
    })
  }

  _setAllowedSenders(conversation) {
    const { counterParty, counterPartyType, organization } = conversation
    const currentUser = this.host.currentUser
    const defaultSenders = [currentUser]

    if (currentUser.contactsMessagingBlocked && organization.isContacts) {
      conversation.allowedSenders = []
      conversation.allowedSendersReason = Reason.CONTACTS_MESSAGING_BLOCKED_BY_ADMIN
      return
    } else if (!counterPartyType || !organization) {
      conversation.allowedSenders = defaultSenders
      conversation.allowedSendersReason = Reason.CONVERSATION_NOT_FULLY_LOADED
      return
    } else if (!counterParty) {
      conversation.allowedSenders = []
      conversation.allowedSendersReason = Reason.NO_COUNTER_PARTY
      return
    }

    switch (counterPartyType) {
      case 'user':
        if (counterParty.$placeholder) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.COUNTER_PARTY_NOT_FULLY_LOADED
          return
        } else if (counterParty.displayName === 'TigerPage') {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.COUNTER_PARTY_IS_TIGERPAGE
          return
        } else {
          conversation.allowedSenders = defaultSenders
          conversation.allowedSendersReason = Reason.NONE
          return
        }

      case 'distributionList':
        conversation.allowedSenders = defaultSenders
        conversation.allowedSendersReason = Reason.NONE
        return

      case 'group':
        const {
          createdByRole,
          createdByUser,
          groupType,
          p2pRecipient,
          p2pSender,
          targetRole,
          targetUser,
        } = counterParty
        let { members, proxiedMembers } = counterParty
        const { allowedSenders: orgAllowedSenders } = organization

        if (groupType === GroupType.FORUM) {
          conversation.allowedSenders = defaultSenders
          conversation.allowedSendersReason = Reason.NONE
          return
        } else if (groupType === GroupType.ROLE_ASSIGNMENT) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.IS_ROLE_ASSIGNMENT_GROUP
          return
        } else if (groupType === GroupType.ROLE_P2P && p2pRecipient && p2pSender) {
          const extraMembers = []
          const createdByBotUser = createdByRole && createdByRole.botUser
          const targetBotUser = targetRole && targetRole.botUser

          if (createdByBotUser && !(members && members.includes(createdByBotUser))) {
            extraMembers.push(createdByBotUser)
          } else if (createdByUser && !(members && members.includes(createdByUser))) {
            extraMembers.push(createdByUser)
          }
          if (targetBotUser && !(members && members.includes(targetBotUser))) {
            extraMembers.push(targetBotUser)
          } else if (targetUser && !(members && members.includes(targetUser))) {
            extraMembers.push(targetUser)
          }
          if (extraMembers.length > 0) members = [...(members || []), ...extraMembers]
        } else if (counterParty.$placeholder) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.COUNTER_PARTY_NOT_FULLY_LOADED
          return
        }

        if (!members) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.GROUP_MEMBERS_NOT_LOADED
          return
        } else if (members.length === 0) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.GROUP_HAS_NO_MEMBERS
          return
        }

        const allowedSenders = []
        for (const entity of orgAllowedSenders) {
          const userEntity = entity.$entityType === 'role' ? entity.botUser : entity
          if (!members.includes(userEntity)) continue

          const { id: userId } = userEntity
          const proxiedEntries = proxiedMembers && proxiedMembers[userId]
          if (proxiedEntries) {
            let inGroupAsSelf = false
            for (const { token } of proxiedEntries) {
              if (token === userId) inGroupAsSelf = true
            }
            if (!inGroupAsSelf) continue
          }

          allowedSenders.push(entity)
        }

        if (allowedSenders.length === 0) {
          conversation.allowedSenders = allowedSenders
          conversation.allowedSendersReason = Reason.USER_IS_NOT_MEMBER_OF_GROUP
          return
        } else if (groupType !== GroupType.ROLE_P2P) {
          conversation.allowedSenders = allowedSenders
          conversation.allowedSendersReason = Reason.NONE
          return
        }

        if (!p2pSender || !allowedSenders.includes(p2pSender)) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.USER_IS_NOT_IN_THIS_ROLE_CONVERSATION
          return
        }

        if (p2pRecipient === currentUser || currentUser.roles.includes(p2pRecipient)) {
          conversation.allowedSenders = []
          conversation.allowedSendersReason = Reason.CONVERSATION_WITH_ROLE_YOU_ARE_CURRENTLY_IN
          return
        } else {
          conversation.allowedSenders = [p2pSender]
          conversation.allowedSendersReason = Reason.NONE
          return
        }

      default:
        conversation.allowedSenders = []
        conversation.allowedSendersReason = Reason.COUNTER_PARTY_TYPE_NOT_SUPPORTED
        return
    }
  }

  reactToProcessingStart({ data }) {
    data = jsonCloneDeep(data)
    const {
      action_type,
      conversation_id: conversationId,
      entity,
      organization_id: organizationId,
    } = data
    const { token: entityId } = entity

    const actionType = ProcessingActionTypes.resolve(action_type)

    this.host.products._addProcessingEvent({
      actionType,
      entity: { id: entityId, ...entity },
    })

    if (actionType === ProcessingActionTypes.ROLE_OPT_OUT) {
      this._roleConversationsProcessingEvent[entityId] = this.host.roles._roleConversations[entityId]
    } else if (actionType === ProcessingActionTypes.ORGANIZATION_MESSAGE_STATUS || actionType === ProcessingActionTypes.ORGANIZATION_REPLAY) {
      const organization = this.host.organizations.getById(organizationId)
      if (!organization) return
      if (organization._markingAsReadConversations) this.__clearOptimisticStatus(organization._markingAsReadConversations)
    } else if (actionType === ProcessingActionTypes.GROUP_REPLAY) {
      let conversation
      if (conversationId) {
        conversation = this.getById(conversationId)
      }
      if (!conversation) return
      this.__clearOptimisticStatus([conversation])
    } else if (actionType === ProcessingActionTypes.ROLE_OPT_IN || actionType === ProcessingActionTypes.ROLE_OPT_OUT) {
      const roleConversations = this.host.roles._roleConversations[entityId]
      if (!roleConversations) return
      this.__clearOptimisticStatus(roleConversations)
    }
  }

  reactToProcessingStop({ data }) {
    data = jsonCloneDeep(data)
    let {
      action_type,
      conversation_id: conversationId,
      entity,
      organization_id: organizationId,
    } = data

    if (!conversationId && entity.conversation_id) {
      conversationId = entity.conversation_id
      delete entity.conversation_id
    }

    if (!organizationId && entity.organization_key) {
      organizationId = entity.organization_key
      delete entity.organization_key
    }

    const { id: entityId } = entity
    const actionType = ProcessingActionTypes.resolve(action_type)

    this.host.products._removeProcessingEvent({
      actionType,
      entity: { id: entityId, ...entity },
    })

    if (actionType === ProcessingActionTypes.GROUP_REPLAY) {
      let conversation

      if (conversationId) {
        conversation = this.getById(conversationId)
      }

      if (!conversation) return

      this.clearTimeline(conversation)
      this.__clearOptimisticStatus([conversation])
    } else if (actionType === ProcessingActionTypes.ROLE_OPT_IN || actionType === ProcessingActionTypes.ROLE_OPT_OUT) {
      this.clearTimelineForAllRoleConversations(actionType, entityId)
      const roleConversations = this.host.roles._roleConversations[entityId]
      if (!roleConversations) return
      this.__clearOptimisticStatus(roleConversations)
    } else if (actionType === ProcessingActionTypes.ORGANIZATION_MESSAGE_STATUS) {
      this.clearMessageStatusesForOrganization(organizationId)
    } else if (actionType === ProcessingActionTypes.CONVERSATION_MESSAGE_STATUS) {
      let conversation

      if (conversationId) {
        conversation = this.getById(conversationId)
      }

      if (!conversation) return

      this.clearMessageStatuses(conversation)
    }
    if (actionType === ProcessingActionTypes.ORGANIZATION_MESSAGE_STATUS || actionType === ProcessingActionTypes.ORGANIZATION_REPLAY) {
      const organization = this.host.organizations.getById(organizationId)
      if (!organization) return
      if (organization._markingAsReadConversations) this.__clearOptimisticStatus(organization._markingAsReadConversations)
    }
  }

  __clearOptimisticStatus(conversations) {
    for (let conversation of conversations) {
      if (conversation._markingAsRead) {
        conversation = this.getById(conversation.id)

        if (conversation) {
          conversation._markingAsRead = false
          conversation._markingAsReadSortNumber = null
          conversation._markingAsReadExpiration = null

          this.host.models.Conversation.inject(conversation)
        }
      }
    }
  }

  clearTimelineForAllRoleConversations(actionType, roleId) {
    let conversations

    if (actionType === ProcessingActionTypes.ROLE_OPT_OUT) {
      conversations = this._roleConversationsProcessingEvent[roleId]
    } else {
      conversations = this.host.roles._roleConversations[roleId]
    }

    if (!conversations) return

    for (const conversation of conversations) {
      this.clearTimeline(conversation)
    }
  }

  clearMessageStatusesForOrganization(organizationId) {
    const organization = this.host.organizations.getById(organizationId)
    if (!organization) return

    const { conversations } = organization
    if (!conversations) return

    for (const conversation of conversations) {
      this.clearMessageStatuses(conversation)
    }
  }

  clearMessageStatuses(conversation, { emit=true }={}) {
    const { content } = conversation

    for (const message of content) {
      message.shouldEnsureRecipientStatus = true
      this.host.models.Message.inject(message)

      setTimeout(() => {
        if (!this.host.messages._pendingFindRecipientStatuses) return

        const cancel = this.host.messages._pendingFindRecipientStatuses[message.id]
        if (cancel) cancel()
      }, 0)
    }

    if (emit) this.emit('clearMessageStatuses', content)

    if (!this.config.condensedReplays) {
      this.__reloadConversation(conversation, { notify: false })
    }

    return content.length
  }

  async mute(conversationId: string|Object, durationInMinutes: number) {
    await this.host.mute.muteConversation(conversationId, durationInMinutes)
  }

  async unmute(conversationId: string|Object) {
    await this.host.mute.unmuteConversation(conversationId)
  }

  async unmuteAll() {
    await this.host.mute.unmuteAll()
  }
}
